import React from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { sessionAPI } from "../services/api";
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import moment from 'moment';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { FaQuestion } from 'react-icons/fa';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MonthlyAssessmentResults = () => {

  const [aiAnalysis, setAIAnalysis] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const { sessionId } = useParams();

  useEffect(() => {
    const fetchAIAnalysis = async () => {
      try {
        setLoading(true);
        const response = await sessionAPI.getAIAnalysis(sessionId);
 
        setAIAnalysis(response.data.aiResponse); 
        setPatient(response.data.patient);
      } catch (error) {
        console.error('Error fetching AI analysis:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAIAnalysis();
  }, [sessionId]);

  const capitalize = (str) => str?.charAt(0).toUpperCase() + str?.slice(1);
  // Hardcoded data based on the image
  const patientData = {
    name: "Sarah Johnson",
    patientId: "Patient ID: #PT-2024-0123",
    age: "32 years",
    gender: "Female",
    profileImage: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=150&auto=format&fit=crop",
    checkInDate: "March 15, 2024",
    checkInTime: "10:00 AM",
    duration: "45 minutes",
    riskScore: 78,
    riskConfidence: "95%",
    riskFactors: [
      { level: "Severe", text: "Sleep Disruption", color: "red" },
      { level: "Moderate", text: "Mood Fluctuation", color: "yellow" },
      { level: "Moderate", text: "Medication Adherence Issues", color: "yellow" },
      { level: "Mild", text: "Social Withdrawal", color: "yellow" }
    ],
    dataCompleteness: {
      percentage: 80,
      quality: "Good",
      lastUpdated: "2 hours ago"
    }
  };

  // Chart data for Mood Trend
  const moodTrendData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [{
      label: 'Mood Score',
      data: aiAnalysis?.trendAnalysis?.metrics?.mood,
      borderColor: 'rgb(75, 192, 192)',
      tension: 0.4,
      fill: false
    }]
  };

  // Chart data for Sleep Pattern
  const sleepPatternData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [{
      label: 'Hours of Sleep',
      data: aiAnalysis?.trendAnalysis?.metrics?.sleep,
      borderColor: 'rgb(54, 162, 235)',
      tension: 0.4,
      fill: false
    }]
  };

  // Chart data for Focus & Productivity
  const focusProductivityData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [{
      label: 'Productivity Score',
      data: aiAnalysis?.trendAnalysis?.metrics?.productivity,
      borderColor: 'rgb(153, 102, 255)',
      tension: 0.4,
      fill: false
    }]
  };

  // Chart data for Medication Adherence
  const medicationAdherenceData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [{
      label: 'Focus Rate',
      data: aiAnalysis?.trendAnalysis?.metrics?.focus,
      backgroundColor: 'rgb(54, 162, 235)',
    }]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: false
      }
    }
  };
  const getImageUrl = (imagePath) => {
    // Return default image for null, undefined, or "null" string
    if (!imagePath || imagePath === "null") return 'https://cdn-icons-png.flaticon.com/512/149/149071.png';
    if (imagePath.startsWith('http')) return imagePath;

    const baseUrl = process.env.REACT_APP_BASE_URL || 'https://backend.adhdrapidcare.com';
    return imagePath.startsWith('/') ? `${baseUrl}${imagePath}` : `${baseUrl}/${imagePath}`;
  };
  return (
    <>
    {loading ? (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    ) : (
    <div className="p-6 max-w-7xl mx-auto bg-gray-50">
      {/* Main Heading */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-800">AI Monitoring & Risk Analysis Engine</h1>
      </div>

      
          {/* Header Cards Row */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {/* Patient Info Card */}
            <div className=" bg-white rounded-lg p-5 shadow">
              <div className="flex items-start gap-3">
                {/* Profile Image */}
                <img 
                    src={getImageUrl(patient?.patientId?.profileImage)}
                    alt={patient?.patientId?.name || 'Patient'}
                    className="w-10 h-10 rounded-full object-cover"
                    onError={(e) => {
                      console.log('Image failed to load:', e);
                      e.target.src = 'https://cdn-icons-png.flaticon.com/512/149/149071.png';
                    }}
                />
                
                {/* Patient Info */}
                <div className="flex flex-col">
                  <h1 className="text-base font-semibold mb-0.5">{patient?.patientId?.name}</h1>
                  <div className="text-xs text-gray-500">{patient?.patientId?._id}</div>
                  <div className="flex items-center text-xs text-gray-500 mt-0.5">
                    <span>{patient?.patientId?.email}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Check-in Info Card */}
            <div className="bg-white rounded-lg p-5 shadow">
              <div className="flex items-start gap-3">
                {/* Check-in Icon */}
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>

                {/* Check-in Info */}
                <div className="flex flex-col">
                  <h2 className="text-base font-semibold mb-0.5">Monthly Check-in</h2>
                  <div className="flex items-center text-xs text-gray-500">
                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {moment(aiAnalysis?.createdAt).format('MMM D, YYYY')}
                  </div>
                  <div className="flex items-center text-xs text-gray-500 mt-0.5">
                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Duration: 10 min
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Risk Level, Factors, and Data Completeness */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            {/* Risk Level */}
            <div className="bg-white rounded-lg p-6 shadow">
                <h2 className="text-base font-semibold mb-6 flex items-center gap-2">
                  Risk Level
                  <span
                    className="w-4 h-4 border border-black rounded-full flex items-center justify-center"
                    data-tooltip-id="risk-tooltip"
                    data-tooltip-content="Patient risk level 0-100. Green = stable, Yellow = monitor, Red = needs attention"
                  >
                    <FaQuestion className="text-black" size={8} />
                  </span>
                </h2>
              <div className="flex flex-col items-center">
                <div className="relative w-32 h-32 mb-2">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    {/* Dashed circle background */}
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="#FF4D4F"
                      strokeWidth="2"
                      strokeDasharray="4 4"
                      className="opacity-30"
                    />
                    {/* Solid circle overlay */}
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="#FF4D4F"
                      strokeWidth="2"
                      strokeDasharray="20"
                      strokeDashoffset="100"
                      className="opacity-100"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold text-[#FF4D4F]">{aiAnalysis?.riskAssessment?.score}</span>
                    <span className="text-sm text-gray-500">Risk Score</span>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-[#FF4D4F] font-medium text-sm">{capitalize(aiAnalysis?.riskAssessment?.level)} Risk</div>
                  <div className="text-xs text-gray-500">{aiAnalysis?.confidenceScore} Confidence</div>
                </div>
              </div>
            </div>

            {/* Risk Factors */}
            <div className="bg-white rounded-lg p-6 shadow">
              <h2 className="text-base font-semibold mb-6">Areas of Concern</h2>
              <div className="space-y-4">

                {aiAnalysis?.riskAssessment?.riskFactors?.map((factor) => (
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="text-sm text-gray-600">{factor}</span>
                </div>
                ))}
              </div>
            </div>

            {/* Data Completeness */}
            <div className="bg-white rounded-lg p-6 shadow">
                <h2 className="text-base font-semibold mb-6 flex items-center gap-2">
                  Data Completeness
                  <span
                    className="w-4 h-4 border border-black rounded-full flex items-center justify-center"
                    data-tooltip-id="risk-tooltip"
                    data-tooltip-content="Patient risk level 0-100. Green = stable, Yellow = monitor, Red = needs attention"
                  >
                    <FaQuestion className="text-black" size={8} />
                  </span>
                </h2>
              <div className="space-y-4">
                <div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{ width: `${aiAnalysis?.dataCompleteness?.percentage}%` }}
                    ></div>
                  </div>
                  <div className="mt-2 text-sm text-gray-600">{aiAnalysis?.dataCompleteness?.percentage}% Complete</div>
                </div>
                <div className="flex items-center text-sm text-green-600">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Data Quality: {aiAnalysis?.dataCompleteness?.quality}
                </div>
                <div className="text-sm text-gray-500">
                  Last Updated: 2 hours ago
                </div>
              </div>
            </div>
          </div>

         

          {/* Charts Grid */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-6 shadow">
              <h2 className="text-lg font-semibold mb-4">Mood Trend</h2>
              <Line data={moodTrendData} options={chartOptions} />
            </div>

            <div className="bg-white rounded-lg p-6 shadow">
              <h2 className="text-lg font-semibold mb-4">Sleep Pattern</h2>
              <Line data={sleepPatternData} options={chartOptions} />
            </div>

            <div className="bg-white rounded-lg p-6 shadow">
              <h2 className="text-lg font-semibold mb-4">Productivity</h2>
              <Line data={focusProductivityData} options={chartOptions} />
            </div>

            <div className="bg-white rounded-lg p-6 shadow">
              <h2 className="text-lg font-semibold mb-4">Focus</h2>
              <Bar data={medicationAdherenceData} options={chartOptions} />
            </div>
          </div> */}

          {/* Priority Topics, Questions, and Actions Section */}
          <div className="grid grid-cols-3 gap-6 mt-6">
            {/* Priority Topics */}
            <div className="bg-white rounded-lg p-6 shadow">
              <h2 className="text-base font-semibold mb-4">Key Discussion Points</h2>
              <div className="space-y-3">
                {aiAnalysis?.sessionPreparation?.priorityDiscussionTopics?.map((topic) => (
                <div className="flex items-center gap-2 text-blue-600 cursor-pointer">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-sm">{topic}</span>
                </div>
                ))}
              </div>
            </div>

            {/* Suggested Questions */}
            <div className="bg-white rounded-lg p-6 shadow">
              <h2 className="text-base font-semibold mb-4">Questions to Ask</h2>
              <div className="space-y-4">
                {aiAnalysis?.sessionPreparation?.suggestedQuestions?.map((question, index) => (
                <div className="flex gap-3">
                  <span className="text-blue-600 font-medium">{index + 1}.</span>
                  <p className="text-sm text-gray-600">{question}</p>
                </div>
                ))}
              </div>
            </div>

            {/* Recommended Actions */}
            <div className="bg-white rounded-lg p-6 shadow">
              <h2 className="text-base font-semibold mb-4">Next Steps</h2>
              <div className="space-y-3">
                {aiAnalysis?.sessionPreparation?.recommendedActions?.map((action, index) => (
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-medium">{index + 1}.</span>
                  <span className="text-sm text-gray-600">{action}</span>
                </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow">
              <h2 className="text-base font-semibold mb-4">Pattern Insights</h2>
              <div className="space-y-3">
                {aiAnalysis?.trendAnalysis?.patternInsights?.map((insight, index) => (
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-medium">{index + 1}.</span>
                  <span className="text-sm text-gray-600">{insight}</span>
                </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow">
              <h2 className="text-base font-semibold mb-4">Areas Needing Attention</h2>
              <div className="space-y-3">
                {aiAnalysis?.trendAnalysis?.concerningPatterns?.map((pattern, index) => (
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-medium">{index + 1}.</span>
                  <span className="text-sm text-gray-600">{pattern}</span>
                </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow">
              <h2 className="text-base font-semibold mb-4">What's Going Well</h2>
              <div className="space-y-3">
                {aiAnalysis?.trendAnalysis?.positivePatterns?.map((pattern, index) => (
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-medium">{index + 1}.</span>
                  <span className="text-sm text-gray-600">{pattern}</span>
                </div>
                ))}
              </div>
            </div>
          </div>

         

          {/* AI Summary Section */}
          <div className="mt-6 bg-gray-50 rounded-lg ">
            <div className='bg-gray-100 p-5 rounded-lg'>
            <h2 className="text-base font-semibold mb-3">AI Summary</h2>
            <p className="text-sm text-gray-600 mb-4">
              {aiAnalysis?.analysisSummary}
              </p>
            </div>
            
          </div>
      </div>
      )}
      <ReactTooltip id="risk-tooltip" />
      <ReactTooltip id="data-tooltip" />

    </>

  );
};

export default MonthlyAssessmentResults; 